import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { CrossmarkIcon, DownloadIcon } from 'assets/icons';
import { IconWrapper, LinkText, MainTitle } from 'common/components';
import { ImageDocumentMemoized, MemoizedDocument, useFileUrl } from 'common/components/Document';
import { useScreenSizeDetection } from 'common/hooks';
import { RegularModal } from 'components/common';
import { DotsLoader } from 'components/common/DotsLoader';
import { Flex } from 'components/styled/Flex';
import { useCustomToast } from 'hooks';
import { imageExtensions } from 'modules/accommodations/features/rentals/CreateRental/Contracts/components/consts';

export const DocumentModalPreview = ({
  pdfDocument,
  pdfIsLoading,
  pdfIsError,
  closeModalPreview,
  documentExtension,
  modalMaxWidth,
  error,
}: {
  pdfDocument: Blob | MediaSource;
  pdfIsLoading: boolean;
  pdfIsError: boolean;
  error?: any;
  closeModalPreview: () => void;
  documentExtension: string;
  modalMaxWidth?: string;
}) => {
  const { t } = useTranslation();
  const { errorToast } = useCustomToast();

  const { colors, fontFamily, fontSize } = useTheme();

  const { isMobile } = useScreenSizeDetection();

  const isImage = imageExtensions?.includes(documentExtension);

  const { fileUrl } = useFileUrl({ pdfBlob: pdfDocument });

  useEffect(() => {
    if (pdfIsError) {
      if (error?.message.includes('404')) {
        errorToast(t('The requested PDF invoice was not found'));
      }

      closeModalPreview();
    }
  }, [pdfIsError]);

  if (pdfIsError) return <></>;

  return (
    <RegularModal
      showModal
      modalMaxWidth={modalMaxWidth || '50%'}
      onHideHandler={closeModalPreview}
    >
      <>
        {!isMobile || isImage ? (
          <Flex justifyContent="space-between" margin="0 0 2rem 0">
            <MainTitle fontFamily={fontFamily.semiBold} fontSize="1.125rem">
              {t('Show document')}
            </MainTitle>

            <IconWrapper $stroke={colors.black_80} onClick={closeModalPreview}>
              <CrossmarkIcon />
            </IconWrapper>
          </Flex>
        ) : null}

        {pdfIsLoading && <DotsLoader />}

        {fileUrl && !pdfIsLoading && !isImage && !isMobile && (
          <Flex justifyContent="flex-end" margin="0 0 1rem 0">
            <LinkText
              href={fileUrl}
              download="Document.pdf"
              color={colors.primary_dark}
              fontFamily={fontFamily.medium}
              fontSize={fontSize.md}
            >
              <Flex alignItems="center" gap="0.5rem" style={{ cursor: 'pointer' }}>
                <DownloadIcon stroke={colors.primary_dark} width={20} height={20} />
                {t('Download document')}
              </Flex>
            </LinkText>
          </Flex>
        )}

        {isImage && !pdfIsLoading && pdfDocument && (
          <ImageDocumentMemoized src={pdfDocument} title="Document" margin="0 0 1rem 0" />
        )}

        {!isImage && !isMobile && !pdfIsLoading && pdfDocument && (
          <MemoizedDocument src={pdfDocument} title="Document" />
        )}

        {!isImage && isMobile && !pdfIsLoading && pdfDocument && (
          <ImageDocumentMemoized margin="0" src={pdfDocument} title="Document" disableAsImageBtn />
        )}
      </>
    </RegularModal>
  );
};
